/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { Progress } from "@/components/ui/progress";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from "recharts";
import { Download, Filter, Calendar, TrendingUp, ArrowUp, ArrowDown, Minus, Clock, AlertTriangle, FileText, PieChartIcon } from "lucide-react";
import { Link } from "react-router-dom";
import bgImage from "@/assets/favicon.png"
import apiService from "@/services/apiService";
import { useAppStore } from "@/store/appStore";
import { DocumentViewPopup } from "@/components/document-view-popup";
import type { Document } from "@/lib/interfaces";

// Dummy data for daily incidents
const sampleDailyIncidentsData = [
  { date: "2024-01-01", total: 12, theft: 4, assault: 2, fraud: 1, domestic: 3, others: 2 },
  { date: "2024-01-02", total: 15, theft: 5, assault: 3, fraud: 2, domestic: 2, others: 3 },
  { date: "2024-01-03", total: 10, theft: 3, assault: 2, fraud: 1, domestic: 2, others: 2 },
  { date: "2024-01-04", total: 18, theft: 6, assault: 4, fraud: 2, domestic: 3, others: 3 },
  { date: "2024-01-05", total: 14, theft: 5, assault: 2, fraud: 1, domestic: 4, others: 2 },
  { date: "2024-01-06", total: 9, theft: 2, assault: 1, fraud: 2, domestic: 2, others: 2 },
  { date: "2024-01-07", total: 8, theft: 3, assault: 1, fraud: 1, domestic: 1, others: 2 },
  { date: "2024-01-08", total: 16, theft: 6, assault: 3, fraud: 2, domestic: 3, others: 2 },
  { date: "2024-01-09", total: 13, theft: 4, assault: 2, fraud: 3, domestic: 2, others: 2 },
  { date: "2024-01-10", total: 17, theft: 7, assault: 3, fraud: 2, domestic: 3, others: 2 },
  { date: "2024-01-11", total: 11, theft: 4, assault: 2, fraud: 1, domestic: 2, others: 2 },
  { date: "2024-01-12", total: 14, theft: 5, assault: 3, fraud: 2, domestic: 2, others: 2 },
  { date: "2024-01-13", total: 10, theft: 3, assault: 2, fraud: 1, domestic: 2, others: 2 },
  { date: "2024-01-14", total: 9, theft: 3, assault: 1, fraud: 1, domestic: 2, others: 2 },
  { date: "2024-01-15", total: 15, theft: 6, assault: 3, fraud: 2, domestic: 2, others: 2 },
  { date: "2024-01-16", total: 12, theft: 4, assault: 2, fraud: 2, domestic: 2, others: 2 },
  { date: "2024-01-17", total: 16, theft: 5, assault: 4, fraud: 2, domestic: 3, others: 2 },
  { date: "2024-01-18", total: 13, theft: 4, assault: 3, fraud: 1, domestic: 3, others: 2 },
  { date: "2024-01-19", total: 11, theft: 3, assault: 2, fraud: 2, domestic: 2, others: 2 },
  { date: "2024-01-20", total: 14, theft: 5, assault: 3, fraud: 1, domestic: 3, others: 2 },
  { date: "2024-01-21", total: 10, theft: 3, assault: 2, fraud: 1, domestic: 2, others: 2 },
  { date: "2024-01-22", total: 17, theft: 6, assault: 4, fraud: 2, domestic: 3, others: 2 },
  { date: "2024-01-23", total: 15, theft: 5, assault: 3, fraud: 2, domestic: 3, others: 2 },
  { date: "2024-01-24", total: 12, theft: 4, assault: 2, fraud: 1, domestic: 3, others: 2 },
  { date: "2024-01-25", total: 14, theft: 5, assault: 3, fraud: 2, domestic: 2, others: 2 },
  { date: "2024-01-26", total: 18, theft: 7, assault: 4, fraud: 2, domestic: 3, others: 2 },
  { date: "2024-01-27", total: 13, theft: 4, assault: 3, fraud: 1, domestic: 3, others: 2 },
  { date: "2024-01-28", total: 11, theft: 3, assault: 2, fraud: 2, domestic: 2, others: 2 },
  { date: "2024-01-29", total: 16, theft: 6, assault: 3, fraud: 2, domestic: 3, others: 2 },
  { date: "2024-01-30", total: 14, theft: 5, assault: 3, fraud: 1, domestic: 3, others: 2 },
  { date: "2024-01-31", total: 15, theft: 5, assault: 3, fraud: 2, domestic: 3, others: 2 },
];

// Dummy data for monthly trends
// const monthlyTrendsData = [
//   { month: "Jan", year: "2023", total: 320, theft: 120, assault: 65, fraud: 45, domestic: 60, others: 30 },
//   { month: "Feb", year: "2023", total: 290, theft: 110, assault: 60, fraud: 40, domestic: 55, others: 25 },
//   { month: "Mar", year: "2023", total: 310, theft: 115, assault: 62, fraud: 43, domestic: 58, others: 32 },
//   { month: "Apr", year: "2023", total: 340, theft: 125, assault: 70, fraud: 48, domestic: 65, others: 32 },
//   { month: "May", year: "2023", total: 360, theft: 135, assault: 75, fraud: 50, domestic: 68, others: 32 },
//   { month: "Jun", year: "2023", total: 380, theft: 140, assault: 80, fraud: 55, domestic: 70, others: 35 },
//   { month: "Jul", year: "2023", total: 400, theft: 150, assault: 85, fraud: 58, domestic: 72, others: 35 },
//   { month: "Aug", year: "2023", total: 390, theft: 145, assault: 82, fraud: 56, domestic: 71, others: 36 },
//   { month: "Sep", year: "2023", total: 370, theft: 138, assault: 78, fraud: 52, domestic: 68, others: 34 },
//   { month: "Oct", year: "2023", total: 350, theft: 130, assault: 73, fraud: 48, domestic: 65, others: 34 },
//   { month: "Nov", year: "2023", total: 330, theft: 125, assault: 68, fraud: 45, domestic: 62, others: 30 },
//   { month: "Dec", year: "2023", total: 345, theft: 128, assault: 72, fraud: 47, domestic: 64, others: 34 },
//   { month: "Jan", year: "2024", total: 355, theft: 132, assault: 74, fraud: 49, domestic: 66, others: 34 },
// ];

// Dummy data for time of day analysis
const timeOfDayData = [
  { time: "00:00-02:59", incidents: 28, category: "Night" },
  { time: "03:00-05:59", incidents: 15, category: "Night" },
  { time: "06:00-08:59", incidents: 22, category: "Morning" },
  { time: "09:00-11:59", incidents: 45, category: "Morning" },
  { time: "12:00-14:59", incidents: 56, category: "Afternoon" },
  { time: "15:00-17:59", incidents: 68, category: "Afternoon" },
  { time: "18:00-20:59", incidents: 75, category: "Evening" },
  { time: "21:00-23:59", incidents: 46, category: "Night" },
];

// Dummy data for day of week analysis
// const dayOfWeekData = [
//   { day: "Monday", incidents: 58, theft: 22, assault: 12, fraud: 8, domestic: 10, others: 6 },
//   { day: "Tuesday", incidents: 52, theft: 20, assault: 10, fraud: 7, domestic: 9, others: 6 },
//   { day: "Wednesday", incidents: 55, theft: 21, assault: 11, fraud: 8, domestic: 9, others: 6 },
//   { day: "Thursday", incidents: 60, theft: 23, assault: 12, fraud: 9, domestic: 10, others: 6 },
//   { day: "Friday", incidents: 72, theft: 28, assault: 15, fraud: 10, domestic: 12, others: 7 },
//   { day: "Saturday", incidents: 85, theft: 32, assault: 18, fraud: 12, domestic: 15, others: 8 },
//   { day: "Sunday", incidents: 68, theft: 26, assault: 14, fraud: 9, domestic: 12, others: 7 },
// ];

// Dummy data for seasonal trends
const seasonalTrendsData = [
  { season: "Winter", incidents: 950, theft: 360, assault: 200, fraud: 130, domestic: 180, others: 80 },
  { season: "Spring", incidents: 1020, theft: 380, assault: 215, fraud: 140, domestic: 195, others: 90 },
  { season: "Summer", incidents: 1180, theft: 440, assault: 250, fraud: 160, domestic: 220, others: 110 },
  { season: "Autumn", incidents: 1050, theft: 390, assault: 220, fraud: 145, domestic: 200, others: 95 },
];

const COLORS = ["#FF6384","#36A2EB","#FFCE56","#4BC0C0","#9966FF","#FF9F40","#C9CBCF","#FF5A5E","#5AD3D1","#FFC870","#A4DE02","#FF6F61","#6B5B95","#88B04B","#F7CAC9","#92A8D1","#955251","#B565A7","#009B77","#DD4124"];

export default function IncidentTrendsReport() {
  const { _filterOptions } = useAppStore();
  const caseCategories = _filterOptions.caseCategories || [];
  const [notableIncidentsData, setNotableIncidentsData] = useState([]);
  const [policeStationWiseData, setPoliceStationWiseData] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [dailyIncidentsData, setDailyIncidentsData] = useState(sampleDailyIncidentsData);
  const [monthlyTrendsData, setMonthlyTrendsData] = useState<any>([]);
  const [dayOfWeekIncidentsData, setDayOfWeekIncidentsData] = useState([]);
  const [keyFindings, setKeyFindings] = useState<any[]>([]);
  const [recommendations , setRecommendations ] = useState<any[]>([]);
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null)
  useEffect(() => {
    getDateWiseIncident();
    getCaseTypesWithCount();
    getPoliceStationsWithCount();
    getDailyIncidentsCount();
    getMonthlyIncidentsCount();
    getDailyIncidentsByDayOfWeek();
  }, []);

  const getDateWiseIncident = async () => {
    const jsonNotableIncidents:any = await apiService.get('general-diaries/notable-incidents');
    setNotableIncidentsData(jsonNotableIncidents);
  };
  const getCaseTypesWithCount = async () => {
    const jsonCaseTypes:any = await apiService.get('general-diaries/case-types-count');
    setCaseTypes(jsonCaseTypes.map((caseType: any) => (caseType.caseType == "" ? "NA" : caseType.caseType)));
  };
  const getPoliceStationsWithCount = async () => {
    const jsonPoliceStations:any = await apiService.get('general-diaries/police-stations-count');
    const totalCount = jsonPoliceStations.reduce((a: any, c: any) => a + c.count, 0);
    setPoliceStationWiseData(
      jsonPoliceStations.map((data: any) => ({
        ...data,
        percentage: ((100 * data.count) / totalCount).toFixed(2),
      }))
    );
  };
  const getDailyIncidentsCount = async () => {
    const jsonDailyIncidents:any = await apiService.get('general-diaries/daily-incidents');
    setDailyIncidentsData(jsonDailyIncidents);
  };
  const getMonthlyIncidentsCount = async () => {
    const jsonDailyIncidents:any = await apiService.get('general-diaries/monthly-incidents');
    setMonthlyTrendsData(jsonDailyIncidents);
  };
  const getDailyIncidentsByDayOfWeek = async () => {
    const jsonDailyIncidents:any = await apiService.get('general-diaries/day-of-week-incidents');
    const { keyFindings, recommendations } = getInsightsAndRecommendations(jsonDailyIncidents);
    setKeyFindings(keyFindings);
    setRecommendations(recommendations);
    setDayOfWeekIncidentsData(jsonDailyIncidents);
  };

  const [filters, setFilters] = useState({
    dateFrom: "2024-01-01",
    dateTo: "2024-01-31",
    caseType: "",
    policeStation: "",
    timeRange: "daily",
  });

  const timeRanges = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "yearly", label: "Yearly" },
  ];

  // Calculate statistics
  const totalIncidents = dailyIncidentsData.reduce((sum: any, day: any) => sum + day.total, 0);
  const avgDailyIncidents = Math.round(totalIncidents / dailyIncidentsData.length);
  const maxIncidentsDay = dailyIncidentsData.reduce((max: any, day: any) => (day.total > max.total ? day : max), dailyIncidentsData[0]);
  //   const minIncidentsDay = dailyIncidentsData.reduce(
  //     (min, day) => (day.total < min.total ? day : min),
  //     dailyIncidentsData[0],
  //   )

  // Calculate trend (comparing first half of month to second half)
  // const firstHalfAvg = dailyIncidentsData.slice(0, 15).reduce((sum: any, day: any) => sum + day.total, 0) / dailyIncidentsData.slice(0, 15).length;
  // const secondHalfAvg = dailyIncidentsData.slice(15).reduce((sum: any, day: any) => sum + day.total, 0) / dailyIncidentsData.slice(15).length;
  // const trendPercentage = Math.round(((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100);
  // const trendDirection = trendPercentage > 0 ? "up" : trendPercentage < 0 ? "down" : "stable";

  // Format data for charts
  const formattedDailyData = dailyIncidentsData.map((day: any) => ({
    ...day,
    date: new Date(day.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
  }));

  const exportToCSV = () => {
    const headers = ["Date", "Total", caseCategories[0]?.title, caseCategories[1]?.title, caseCategories[2]?.title, caseCategories[3]?.title, caseCategories[4]?.title, caseCategories[5]?.title];
    const csvContent = [headers.join(","), ...dailyIncidentsData.map((row: any) => [row.date, row.total, row[caseCategories[0]?.title], row[caseCategories[1]?.title], row[caseCategories[2]?.title], row[caseCategories[3]?.title], row[caseCategories[4]?.title], row[caseCategories[5]?.title]].join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `incident-trends-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const getInsightsAndRecommendations = (data:any) => {
    const totalPerDay = data.map((d:any) => d.total);
    const totalCases = totalPerDay.reduce((a:any, b:any) => a + b, 0);
    const weekendTotal = data.filter((d:any) => d.day === "Saturday" || d.day === "Sunday").reduce((sum:any, d:any) => sum + d.total, 0);
    const weekdayTotal = totalCases - weekendTotal;
    const weekendSurgePercent = ((weekendTotal / weekdayTotal) * 100).toFixed(1);
    const categoryCounts:any = {};
    data.forEach((d:any) => {
      Object.keys(d).forEach((key) => {
        if (key !== "day" && key !== "total") {
          categoryCounts[key] = (categoryCounts[key] || 0) + d[key];
        }
      });
    });
    const topCategories = Object.entries(categoryCounts).sort((a:any, b:any) => b[1] - a[1]).slice(0, 3);
    const keyFindings = [
      `📊 <strong>Weekend Surge:</strong> ${weekendSurgePercent}% higher incident rate on weekends compared to weekdays.`,
    ];
    topCategories.forEach(([category, count]) => {
      keyFindings.push(
        `🚨 <strong>${category} Pattern:</strong> ${category} cases are significant, totaling ${count} cases in the week.`
      );
    });
    const recommendations = [
      `✔️ Increase patrol presence on Friday and Saturday evenings.`,
      `✔️ Focus operations based on top categories like "${topCategories[0]?.[0]}" and "${topCategories[1]?.[0]}".`,
      `✔️ Implement targeted public awareness campaigns related to "${topCategories[2]?.[0]}".`,
      `✔️ Maintain steady response capability for common issues like "संपत्ति विवाद".`,
    ];
    return { keyFindings, recommendations };
  };

  const handleViewDocument = (document: any) => {
      setViewingDocument(document)
      console.log(document);
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div
        className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-3 pointer-events-none"
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      />
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <Link to="/reports" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
              ← Back to Reports
            </Link>
            <h1 className="text-3xl font-bold text-black">Incident Trends Report</h1>
            <p className="text-gray-600 mt-1">Analyze incident patterns over time with multiple visualizations</p>
          </div>
          <Button onClick={exportToCSV} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{totalIncidents}</div>
                  <p className="text-sm text-gray-600">Total Incidents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">{avgDailyIncidents}</div>
                  <p className="text-sm text-gray-600">Avg Daily Incidents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <div>
                  <div className="text-2xl font-bold text-red-600">{maxIncidentsDay.total}</div>
                  <p className="text-sm text-gray-600">Peak Day: {new Date(maxIncidentsDay.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {/* <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                {trendDirection === "up" ? <ArrowUp className="h-5 w-5 text-red-600" /> : trendDirection === "down" ? <ArrowDown className="h-5 w-5 text-green-600" /> : <Minus className="h-5 w-5 text-gray-600" />}
                <div>
                  <div className={`text-2xl font-bold ${trendDirection === "up" ? "text-red-600" : trendDirection === "down" ? "text-green-600" : "text-gray-600"}`}>
                    {trendPercentage > 0 ? "+" : ""}
                    {JSON.stringify(trendPercentage)}%
                  </div>
                  <p className="text-sm text-gray-600">Monthly Trend</p>
                </div>
              </div>
            </CardContent>
          </Card> */}
        </div>

        {/* Filters */}
        <Card className="border border-gray-200 hidden">
          <CardHeader>
            <CardTitle className="text-lg text-black flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Time Range
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">From Date</Label>
                <Input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} className="border-gray-300" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">To Date</Label>
                <Input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} className="border-gray-300" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Case Type</Label>
                <Select value={filters.caseType} onValueChange={(value) => setFilters({ ...filters, caseType: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select case type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {caseTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Police Station</Label>
                <Select value={filters.policeStation} onValueChange={(value) => setFilters({ ...filters, policeStation: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue placeholder="Select station" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {_filterOptions.policeStations && _filterOptions.policeStations.length > 0 && _filterOptions.policeStations.map((station:any) => (
                      <SelectItem key={station.id} value={station.name}>
                        {station.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-black">Time Range</Label>
                <Select value={filters.timeRange} onValueChange={(value) => setFilters({ ...filters, timeRange: value })}>
                  <SelectTrigger className="border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200">
                    {timeRanges.map((range) => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end">
              <Button className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">Apply Filters</Button>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="daily" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 bg-gray-100">
            <TabsTrigger value="daily" className="data-[state=active]:bg-white">
              Daily Trends
            </TabsTrigger>
            <TabsTrigger value="station-wise" className="data-[state=active]:bg-white">
              Police Station Trends
            </TabsTrigger>
            {/* <TabsTrigger value="time-of-day" className="data-[state=active]:bg-white">
              Time of Day
            </TabsTrigger> */}
            <TabsTrigger value="day-of-week" className="data-[state=active]:bg-white">
              Day of Week
            </TabsTrigger>
            <TabsTrigger value="monthly" className="data-[state=active]:bg-white">
              Monthly Trends
            </TabsTrigger>
            <TabsTrigger value="notable" className="data-[state=active]:bg-white">
              Notable Incidents
            </TabsTrigger>
          </TabsList>

          {/* Daily Trends Tab */}
          <TabsContent value="daily">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Daily Incident Trends ({new Date().toLocaleString("default", { month: "long" })} {new Date().getFullYear()})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={formattedDailyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="date" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="total" stroke="#000000" strokeWidth={2} name="Total" />
                      <Line type="monotone" dataKey={caseCategories[0]?.title} stroke="#1e40af" strokeWidth={1.5} name={caseCategories[0]?.title} />
                      <Line type="monotone" dataKey={caseCategories[1]?.title} stroke="#b91c1c" strokeWidth={1.5} name={caseCategories[1]?.title} />
                      <Line type="monotone" dataKey={caseCategories[2]?.title} stroke="#77950aff" strokeWidth={1.5} name={caseCategories[2]?.title} />
                      <Line type="monotone" dataKey={caseCategories[3]?.title} stroke="#2b623dff" strokeWidth={1.5} name={caseCategories[3]?.title} />
                      <Line type="monotone" dataKey={caseCategories[4]?.title} stroke="#ad0f90ff" strokeWidth={1.5} name={caseCategories[4]?.title} />
                      <Line type="monotone" dataKey={caseCategories[5]?.title} stroke="#af760eff" strokeWidth={1.5} name={caseCategories[5]?.title} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4">Daily Breakdown</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-black font-semibold">Date</TableHead>
                          <TableHead className="text-black font-semibold">Total</TableHead>
                          <TableHead className="text-black font-semibold">{caseCategories[0]?.title}</TableHead>
                          <TableHead className="text-black font-semibold">{caseCategories[1]?.title}</TableHead>
                          <TableHead className="text-black font-semibold">{caseCategories[2]?.title}</TableHead>
                          <TableHead className="text-black font-semibold">{caseCategories[3]?.title}</TableHead>
                          <TableHead className="text-black font-semibold">{caseCategories[4]?.title}</TableHead>
                          <TableHead className="text-black font-semibold">{caseCategories[5]?.title}</TableHead>
                          <TableHead className="text-black font-semibold">Trend</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dailyIncidentsData.slice(0, 10).map((day: any, index: any) => {
                          const prevDay: any = index > 0 ? dailyIncidentsData[index - 1] : null;
                          const trend = prevDay ? (day.total > prevDay.total ? "up" : day.total < prevDay.total ? "down" : "stable") : "stable";
                          const trendPercentage = prevDay ? Math.round(((day.total - prevDay.total) / prevDay.total) * 100) : 0;

                          return (
                            <TableRow key={day.date} className="hover:bg-gray-50">
                              <TableCell className="font-medium text-black">
                                {new Date(day.date).toLocaleDateString("en-IN", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </TableCell>
                              <TableCell className="font-bold text-black">{day.total}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[0]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[1]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[2]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[3]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[4]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[5]?.title] ?? 0}</TableCell>
                              <TableCell>
                                {trend === "up" ? (
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                                    <ArrowUp className="h-3 w-3 mr-1" />
                                    {trendPercentage}%
                                  </Badge>
                                ) : trend === "down" ? (
                                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                                    <ArrowDown className="h-3 w-3 mr-1" />
                                    {Math.abs(trendPercentage)}%
                                  </Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                                    <Minus className="h-3 w-3 mr-1" />
                                    0%
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time of Day Tab */}
          <TabsContent value="time-of-day hidden">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Incident Distribution by Time of Day
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="h-[400px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={timeOfDayData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis dataKey="time" stroke="#666" />
                          <YAxis stroke="#666" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                            }}
                          />
                          <Bar dataKey="incidents" fill="#000000" name="Incidents" radius={[4, 4, 0, 0]} barSize={40} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div>
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-3">Time of Day Analysis</h3>
                        <p className="text-gray-600 mb-4">Incidents are most frequent during evening hours (18:00-20:59), with a secondary peak in the afternoon (15:00-17:59). Early morning hours (03:00-05:59) show the lowest incident rates.</p>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                              <span className="font-medium">Morning (06:00-11:59)</span>
                            </div>
                            <span className="font-bold">{timeOfDayData.filter((item) => item.category === "Morning").reduce((sum, item) => sum + item.incidents, 0)}</span>
                          </div>
                          <Progress value={(timeOfDayData.filter((item) => item.category === "Morning").reduce((sum, item) => sum + item.incidents, 0) / timeOfDayData.reduce((sum, item) => sum + item.incidents, 0)) * 100} className="h-2" />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                              <span className="font-medium">Afternoon (12:00-17:59)</span>
                            </div>
                            <span className="font-bold">{timeOfDayData.filter((item) => item.category === "Afternoon").reduce((sum, item) => sum + item.incidents, 0)}</span>
                          </div>
                          <Progress value={(timeOfDayData.filter((item) => item.category === "Afternoon").reduce((sum, item) => sum + item.incidents, 0) / timeOfDayData.reduce((sum, item) => sum + item.incidents, 0)) * 100} className="h-2" />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                              <span className="font-medium">Evening (18:00-20:59)</span>
                            </div>
                            <span className="font-bold">{timeOfDayData.filter((item) => item.category === "Evening").reduce((sum, item) => sum + item.incidents, 0)}</span>
                          </div>
                          <Progress value={(timeOfDayData.filter((item) => item.category === "Evening").reduce((sum, item) => sum + item.incidents, 0) / timeOfDayData.reduce((sum, item) => sum + item.incidents, 0)) * 100} className="h-2" />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                              <span className="font-medium">Night (21:00-05:59)</span>
                            </div>
                            <span className="font-bold">{timeOfDayData.filter((item) => item.category === "Night").reduce((sum, item) => sum + item.incidents, 0)}</span>
                          </div>
                          <Progress value={(timeOfDayData.filter((item) => item.category === "Night").reduce((sum, item) => sum + item.incidents, 0) / timeOfDayData.reduce((sum, item) => sum + item.incidents, 0)) * 100} className="h-2" />
                        </div>
                      </div>

                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <h4 className="font-medium text-yellow-800 mb-1">Key Insight</h4>
                        <p className="text-sm text-yellow-700">Evening hours (18:00-20:59) account for the highest number of incidents, suggesting a need for increased patrol during these hours. Consider adjusting shift patterns to ensure maximum coverage during peak incident times.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Day of Week Tab */}
          <TabsContent value="day-of-week">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Incident Distribution by Day of Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dayOfWeekIncidentsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="day" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Legend />
                      <Bar dataKey={caseCategories[0]?.title} stackId="a" fill="#1e40af" name={caseCategories[0]?.title} />
                      <Bar dataKey={caseCategories[1]?.title} stackId="a" fill="#b91c1c" name={caseCategories[1]?.title} />
                      <Bar dataKey={caseCategories[2]?.title} stackId="a" fill="#c2410c" name={caseCategories[2]?.title} />
                      <Bar dataKey={caseCategories[3]?.title} stackId="a" fill="#4338ca" name={caseCategories[3]?.title} />
                      <Bar dataKey={caseCategories[4]?.title} stackId="a" fill="#0f766e" name={caseCategories[4]?.title} />
                      <Bar dataKey={caseCategories[5]?.title} stackId="a" fill="#4f5803ff" name={caseCategories[5]?.title} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Day of Week Breakdown</h3>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-black font-semibold">Day</TableHead>
                            <TableHead className="text-black font-semibold">Total</TableHead>
                            <TableHead className="text-black font-semibold">{caseCategories[0]?.title}</TableHead>
                            <TableHead className="text-black font-semibold">{caseCategories[1]?.title}</TableHead>
                            <TableHead className="text-black font-semibold">{caseCategories[2]?.title}</TableHead>
                            <TableHead className="text-black font-semibold">{caseCategories[3]?.title}</TableHead>
                            <TableHead className="text-black font-semibold">{caseCategories[4]?.title}</TableHead>
                            <TableHead className="text-black font-semibold">{caseCategories[5]?.title}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dayOfWeekIncidentsData.map((day:any) => (
                            <TableRow key={day.day} className="hover:bg-gray-50">
                              <TableCell className="font-medium text-black">{day.day}</TableCell>
                              <TableCell className="font-bold text-black">{day.total}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[0]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[1]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[2]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[3]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[4]?.title] ?? 0}</TableCell>
                              <TableCell className="text-gray-600">{day[caseCategories[5]?.title] ?? 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Weekly Pattern Analysis</h3>
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-medium text-blue-800 mb-1">Key Findings</h4>
                      <ul className="text-sm text-blue-700 space-y-2 list-disc pl-4">
                        {keyFindings && keyFindings.length > 0 && keyFindings.map((finding, i) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: finding }} />
                        ))}
                      </ul>
                    </div>

                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <h4 className="font-medium text-green-800 mb-1">Recommendations</h4>
                      <ul className="text-sm text-green-700 space-y-2 list-disc pl-4">
                        {recommendations && recommendations.length > 0 && recommendations.map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                    
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monthly Trends Tab */}
          <TabsContent value="monthly">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Monthly & Seasonal Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Monthly Incident Trends (2023-2024)</h3>
                    <div className="h-[400px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={monthlyTrendsData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis dataKey="month" stroke="#666" tickFormatter={(value, index) => `${value} ${monthlyTrendsData[index].year}`} />
                          <YAxis stroke="#666" />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "white",
                              border: "1px solid #ccc",
                              borderRadius: "4px",
                            }}
                            formatter={(value, name) => [value, name]}
                            labelFormatter={(label, items) => {
                              const item = items[0]?.payload;
                              return item ? `${item.month} ${item.year}` : label;
                            }}
                          />
                          <Legend />
                          <Area type="monotone" dataKey="total" stroke="#000000" fill="#000000" fillOpacity={0.1} strokeWidth={2} name="Total" />
                          <Area type="monotone" dataKey={caseCategories[0]?.title} stroke="#1e40af" fill="#1e40af" fillOpacity={0.1} strokeWidth={1.5} name={caseCategories[0]?.title} />
                          <Area type="monotone" dataKey={caseCategories[1]?.title} stroke="#b91c1c" fill="#b91c1c" fillOpacity={0.1} strokeWidth={1.5} name={caseCategories[1]?.title} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 hidden">
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Seasonal Analysis</h3>
                      <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={seasonalTrendsData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis dataKey="season" stroke="#666" />
                            <YAxis stroke="#666" />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "white",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                              }}
                            />
                            <Legend />
                            <Bar dataKey="incidents" fill="#000000" name="Total Incidents" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-3">Seasonal Patterns</h3>
                        <p className="text-gray-600 mb-4">Incident rates show clear seasonal variations with summer months experiencing the highest number of cases, followed by autumn. Winter months show the lowest incident rates overall.</p>
                      </div>

                      <div className="space-y-4">
                        {seasonalTrendsData.map((season) => (
                          <div key={season.season}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium">{season.season}</span>
                              <span className="font-bold">{season.incidents}</span>
                            </div>
                            <Progress value={(season.incidents / Math.max(...seasonalTrendsData.map((s) => s.incidents))) * 100} className="h-2" />
                          </div>
                        ))}
                      </div>

                      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <h4 className="font-medium text-purple-800 mb-1">Seasonal Insights</h4>
                        <ul className="text-sm text-purple-700 space-y-2 list-disc pl-4">
                          <li>
                            <strong>Summer Peak:</strong> 24% higher incident rate than winter months
                          </li>
                          <li>
                            <strong>Theft Cases:</strong> Highest during summer tourist season
                          </li>
                          <li>
                            <strong>Assault Cases:</strong> Peak during summer and early autumn
                          </li>
                          <li>
                            <strong>Domestic Cases:</strong> More consistent year-round with slight winter increase
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Police station wise Tab */}
          <TabsContent value="station-wise">
            <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black flex items-center gap-2">
                    <PieChartIcon className="h-5 w-5" />
                    Police Station Wise Case Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-5">
                      <h3 className="text-lg font-semibold mb-4">Case Distribution by Police Station</h3>
                      <ul>
                        {policeStationWiseData.map((station:any, index:number) => (
                          <li key={station.policeStation} className="flex items-center mb-2">
                            <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                            <span className="font-medium text-black">{station.policeStation}</span>
                            <span className="ml-auto text-gray-600">{station.count} cases ({station.percentage}%)</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="md:col-span-7 flex justify-center items-center">
                      <ResponsiveContainer width="100%" className={"md:col-span-7"}>
                      <PieChart>
                        <Pie data={policeStationWiseData} cx="50%" cy="50%" label={false} outerRadius={180} fill="#8884d8" dataKey="count">
                          {policeStationWiseData.map((entry, index) => (
                            <Cell key={`cell-${index}-${entry}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                          <Tooltip formatter={(value, name, props) => [`${value} ${name}`, `${props.payload.policeStation}`]} />
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notable Incidents Tab */}
          <TabsContent value="notable">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Notable Incidents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {notableIncidentsData && notableIncidentsData.map((incident: any) => (
                    <div key={incident.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={incident.caseType === caseCategories[0]?.title ? "bg-blue-100 text-blue-800" : incident.caseType === caseCategories[1]?.title ? "bg-red-100 text-red-800" : incident.caseType === caseCategories[2]?.title ? "bg-orange-100 text-orange-800" : incident.caseType === caseCategories[3]?.title ? "bg-purple-100 text-purple-800" : incident.caseType === caseCategories[4]?.title ? "bg-rose-100 text-white-800" : "bg-gray-100 text-gray-800"}>{incident.caseType}</Badge>
                            <Badge className={incident.status === "ACTIVE" ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}>{incident.status}</Badge>
                          </div>
                          <h3 className="text-lg font-semibold text-black">{incident.description}</h3>
                          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              <span>
                                {new Date(incident.date).toLocaleDateString("en-IN", {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              <span>{incident.time}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FileText className="h-4 w-4" />
                              <span>{incident.gdNumber}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                                <circle cx="12" cy="10" r="3" />
                              </svg>
                              <span>{incident.location.name}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <Button className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white" onClick={() => handleViewDocument(incident)}>View Details</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      {/* Document View Popup */}
      {viewingDocument && 
        <DocumentViewPopup
          open={!!viewingDocument}
          onOpenChange={() => setViewingDocument(null)}
          document={viewingDocument}
        />
      }
    </div>
  );
}
